import { supabase } from "@/integrations/supabase/client";
import type {
  DistanceLeg,
  RunSegmentConfig,
  TrackFlag,
  TrackLeg,
  TrackPause,
} from "@shared/gpxTrack";

const BUCKET = "gpx-tracks";
/** Signed URLs are only used for the immediate download on the track page. */
const SIGNED_URL_TTL_S = 300;

/** A stored `gps_track_info` row, with the JSONB columns typed. */
export interface GpsTrackInfo {
  id: number;
  participation_id: number;
  person_id: number;
  file_link: string | null;
  computed_at: string;
  parse_error: string | null;
  start_time: string | null;
  end_time: string | null;
  start_lat: number | null;
  start_lon: number | null;
  start_place: string | null;
  total_distance_m: number | null;
  elapsed_seconds: number | null;
  moving_seconds: number | null;
  elevation_gain_m: number | null;
  elevation_loss_m: number | null;
  avg_pace_s_per_km: number | null;
  max_speed_kmh: number | null;
  point_count: number | null;
  legs: TrackLeg[];
  distance_legs: DistanceLeg[];
  pauses: TrackPause[];
  flags: TrackFlag[];
}

/** An extra file of a member's track (recording stopped and restarted). */
export interface GpsTrackPart {
  part: number;
  file_link: string;
  track_start: string;
}

export interface GpsTrackMember {
  person_id: number;
  name: string;
  /** First file of the track (part 1). */
  gpx_file_link: string | null;
  /** Further files, part 2 onwards; analysed together with the first. */
  gpx_parts?: GpsTrackPart[];
}

export interface GpsTrackParticipation {
  id: number;
  quizrun_id: number;
  quizrun_number: number;
  quizrun_title: string;
  format: "onsite" | "virtual";
  category: string;
  city: string | null;
  country: string | null;
  event_date: string | null;
  gun_time: string | null;
  start_time: string | null;
  segments: RunSegmentConfig[];
  team_name: string | null;
  /** Until when the runner may replace their own track (quizrun end + 2 days). */
  gpx_replace_until: string | null;
}

export interface GpsTrackSubmit {
  segment_index: number;
  think_start: string | null;
  think_end: string | null;
}

export interface GpsTrackOverview {
  participation: GpsTrackParticipation;
  /** Admin or host of the participation's city: may replace GPX / recompute. */
  can_manage: boolean;
  /** Every member of the participation (participants see their teammates too). */
  members: GpsTrackMember[];
  tracks: GpsTrackInfo[];
  submits: GpsTrackSubmit[];
}

/** Authorized GPX status for one runner in a quizrun; no storage path is exposed. */
export interface GpsTrackInventoryMember {
  participation_id: number;
  person_id: number;
  person_name: string;
  has_track: boolean;
  /** Admin or host of this participation's city. */
  can_manage: boolean;
}

/** Public page for one runner's analysed track. */
export const trackPagePath = (participationId: number, personId: number) =>
  `/track/${participationId}?person=${personId}`;

export interface TrackableQuizrun {
  quizrun_id: number;
  quizrun_title: string;
}

/**
 * Everything the track page needs for one participation. Admins and hosts see
 * every participation; a participant sees their own team's tracks (flags withheld).
 */
export async function fetchGpsTrackOverview(
  participationId: number,
): Promise<GpsTrackOverview> {
  const { data, error } = await supabase.rpc("gps_track_overview", {
    p_participation_id: participationId,
  });
  if (error) throw error;
  return data as unknown as GpsTrackOverview;
}

/**
 * Re-run the server-side analysis for one member's track. The edge function
 * reads the raw file itself, so nothing measurable is sent from here.
 */
export async function extractGpsTrack(
  participationId: number,
  personId: number,
): Promise<void> {
  const { error } = await supabase.functions.invoke("extract-gpx-track", {
    body: { participation_id: participationId, person_id: personId },
  });
  if (error) throw error;
}

/** Short-lived download URL for a raw GPX object in the private bucket. */
export async function signGpxUrl(fileLink: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(fileLink, SIGNED_URL_TTL_S);
  if (error) throw error;
  return data.signedUrl;
}

/** GPX status per runner in a quizrun: everyone for admins/hosts, else the caller's team. */
export async function listQuizrunTrackMembers(
  quizrunId: number,
): Promise<GpsTrackInventoryMember[]> {
  const { data, error } = await supabase.rpc("list_quizrun_track_members", {
    p_quizrun_id: quizrunId,
  });
  if (error) throw error;
  return (data ?? []) as GpsTrackInventoryMember[];
}

/** Quizruns containing tracks the caller may view. */
export async function listTrackableQuizruns(): Promise<TrackableQuizrun[]> {
  const { data, error } = await supabase.rpc("list_trackable_quizruns");
  if (error) throw error;
  return (data ?? []) as TrackableQuizrun[];
}

/**
 * Whose track the page opens on: the one asked for in `?person=`, else the
 * viewer's own track, else the first teammate who uploaded one — so a runner
 * who didn't record lands on the team's route rather than an empty page.
 */
export function defaultTrackPerson(
  members: GpsTrackMember[],
  requested: number | null,
  viewer: number | null,
): number | null {
  if (requested && members.some((m) => m.person_id === requested)) {
    return requested;
  }
  const own = members.find((m) => m.person_id === viewer);
  if (own?.gpx_file_link) return own.person_id;
  return (
    members.find((m) => m.gpx_file_link)?.person_id ??
    own?.person_id ??
    members[0]?.person_id ??
    null
  );
}
